import { useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { games, categories } from '@/mocks/games';

export default function CategorySlugPage() {
  const { slug } = useParams<{ slug: string }>();
  const { pathname } = useLocation();
  const category = categories.find((c) => c.slug === slug);
  const categoryGames = games.filter((g) => g.category === slug);

  useEffect(() => {
    if (category) {
      document.title = `${category.name} - 18网页游戏`;
    }
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [pathname, category]);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading text-foreground-900 mb-3">分类未找到</h1>
            <Link to="/category" className="text-primary-500 hover:text-primary-600 text-sm">返回分类列表</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.18webgame.com' },
      { '@type': 'ListItem', position: 2, name: '游戏分类', item: 'https://www.18webgame.com/category' },
      { '@type': 'ListItem', position: 3, name: category.name, item: `https://www.18webgame.com/category/${slug}` },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <Navbar />
      <main className="pt-16">
        <section className="w-full py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/category" className="hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">游戏分类</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-900 font-medium whitespace-nowrap">{category.name}</span>
            </nav>
            <div className="flex items-center gap-4 mb-3">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-accent-100/70 flex items-center justify-center">
                <i className={`${category.icon} text-2xl md:text-3xl text-accent-600`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-950">{category.name}</h1>
                <p className="text-sm text-foreground-500 mt-1">{category.description} · 共 {categoryGames.length} 款游戏</p>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
              {categoryGames.map((game) => (
                <div
                  key={game.id}
                  className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-300/40 transition-all duration-300"
                >
                  <div className="relative w-full aspect-[3/2] overflow-hidden">
                    <img
                      src={game.image}
                      alt={game.name}
                      title={`${game.name} - ${category.name}网页游戏`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    {game.featured && (
                      <div className="absolute top-2 left-2 bg-accent-500 text-background-50 text-xs px-2 py-0.5 rounded-full whitespace-nowrap">
                        推荐
                      </div>
                    )}
                    <div className="absolute top-2 right-2 bg-background-950/75 backdrop-blur-sm text-background-50 text-xs px-2 py-1 rounded-full whitespace-nowrap">
                      <i className="ri-star-fill text-primary-400 mr-0.5"></i>
                      {game.rating}
                    </div>
                  </div>
                  <div className="p-3 md:p-4">
                    <h3 className="text-sm md:text-base font-semibold text-foreground-900 line-clamp-1">
                      {game.name}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1.5 line-clamp-2 leading-relaxed">
                      {game.description}
                    </p>
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      {game.tags.map((tag) => (
                        <span key={tag} className="text-xs px-2 py-0.5 bg-secondary-100 text-secondary-800 rounded-full whitespace-nowrap">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-1 mt-2 text-xs text-foreground-400">
                      <i className="ri-user-line"></i>
                      <span>{game.players} 玩家</span>
                    </div>
                  </div>
                </div>
              ))}
              {categoryGames.length === 0 && (
                <div className="col-span-full text-center py-16">
                  <p className="text-foreground-500">该分类暂无游戏，敬请期待</p>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}